<template>
  <v-dialog v-model="stickyBoardStore.open" fullscreen :scrim="false" no-click-animation class="sticky-board-dialog">
    <div ref="board" class="sticky-board" data-testid="StickyBoard">
      <action-cable :params="{ channel: 'TeacherStickyNotesChannel' }" @sticky_note_submitted="stickyBoardStore.receiveNote" />

      <div v-if="board.sections_count" class="sticky-board-sections" :class="`count-${board.sections_count}`">
        <div
          v-for="(section, index) in visibleSections"
          :key="index"
          class="sticky-board-section"
          :style="{ backgroundColor: sectionColor(section.color) }"
          data-testid="StickyBoard-section"
        >
          <span v-if="section.title" class="sticky-board-section-title">{{ section.title }}</span>
        </div>
      </div>

      <sticky-board-note v-for="note in board.notes" :key="note.id" :note="note" :show-name="board.show_names" />

      <sticky-board-panel @download="downloadImage" />
    </div>
  </v-dialog>
</template>

<script lang="ts">
import { useTeacherToolsScrimClip } from "~/composables/useTeacherToolsScrimClip"
import { mixColor, useStickyBoardStore } from "~/stores/stickyBoardStore"
import type { StickyBoard, StickySection } from "~/types/stickyBoard"

export default {
  setup() {
    const stickyBoardStore = useStickyBoardStore()
    const scrimClip = useTeacherToolsScrimClip(".sticky-board-dialog", ".sticky-board")
    return { stickyBoardStore, ...scrimClip }
  },

  computed: {
    board(): StickyBoard {
      return this.stickyBoardStore.board
    },

    visibleSections(): StickySection[] {
      return this.board.sections.slice(0, this.board.sections_count ?? 0)
    },
  },

  watch: {
    "stickyBoardStore.open"(isOpen: boolean) {
      if (isOpen) {
        this.$nextTick(() => this.startScrimClipObserver())
      } else {
        this.stopScrimClipObserver()
      }
    },
  },

  unmounted() {
    this.stopScrimClipObserver()
  },

  methods: {
    sectionColor(color: string): string {
      return mixColor(color, 255, 0.6)
    },

    downloadImage() {
      const boardElement = this.$refs.board as HTMLElement
      const boardRect = boardElement.getBoundingClientRect()
      const scale = 2
      const canvas = document.createElement("canvas")
      canvas.width = boardRect.width * scale
      canvas.height = boardRect.height * scale
      const context = canvas.getContext("2d")
      if (!context) { return }

      context.scale(scale, scale)
      context.fillStyle = "#FFFFFF"
      context.fillRect(0, 0, boardRect.width, boardRect.height)

      const relativeRect = (element: Element) => {
        const rect = element.getBoundingClientRect()
        return { x: rect.left - boardRect.left, y: rect.top - boardRect.top, width: rect.width, height: rect.height }
      }

      boardElement.querySelectorAll(".sticky-board-section").forEach((section) => {
        const rect = relativeRect(section)
        context.fillStyle = getComputedStyle(section).backgroundColor
        context.fillRect(rect.x, rect.y, rect.width, rect.height)
      })

      boardElement.querySelectorAll(".sticky-board-section-title").forEach((title) => {
        const rect = relativeRect(title)
        const style = getComputedStyle(title)
        context.fillStyle = "#FFFFFF"
        context.beginPath()
        context.roundRect(rect.x, rect.y, rect.width, rect.height, 6)
        context.fill()
        context.font = style.font
        context.fillStyle = style.color
        context.textAlign = "center"
        context.textBaseline = "middle"
        context.fillText(title.textContent ?? "", rect.x + rect.width / 2, rect.y + rect.height / 2)
      })

      context.textAlign = "left"
      context.textBaseline = "top"
      const notesBottomUp = [...this.board.notes].sort((a, b) => a.z - b.z)
      notesBottomUp.forEach((note) => {
        const element = boardElement.querySelector(`[data-note-id="${note.id}"]`)
        if (!element) { return }

        const rect = relativeRect(element)
        const header = relativeRect(element.querySelector(".sticky-note-header") as Element)
        context.save()
        context.beginPath()
        context.roundRect(rect.x, rect.y, rect.width, rect.height, 6)
        context.clip()
        context.fillStyle = note.color
        context.fillRect(rect.x, rect.y, rect.width, rect.height)
        context.fillStyle = mixColor(note.color, 0, 0.92)
        context.fillRect(header.x, header.y, header.width, header.height)
        context.restore()

        if (note.highlighted) {
          context.strokeStyle = "#4A4A3A"
          context.lineWidth = 2
          context.beginPath()
          context.roundRect(rect.x + 1, rect.y + 1, rect.width - 2, rect.height - 2, 6)
          context.stroke()
        }

        const name = element.querySelector(".sticky-note-name")
        if (name) {
          const nameRect = relativeRect(name)
          context.font = getComputedStyle(name).font
          context.fillStyle = "rgba(0, 0, 0, 0.45)"
          context.textAlign = "right"
          context.fillText(name.textContent ?? "", nameRect.x + nameRect.width - 10, nameRect.y + 4)
          context.textAlign = "left"
        }

        const textarea = element.querySelector(".sticky-note-text") as HTMLTextAreaElement
        const textStyle = getComputedStyle(textarea)
        const textRect = relativeRect(textarea)
        const lineHeight = parseFloat(textStyle.lineHeight)
        context.font = textStyle.font
        context.fillStyle = textStyle.color
        this.wrapText(context, note.text, textRect.width - 20).forEach((line, index) => {
          context.fillText(line, textRect.x + 10, textRect.y + 8 + index * lineHeight)
        })
      })

      canvas.toBlob((blob) => {
        if (!blob) { return }
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = "limlapas.png"
        link.click()
        URL.revokeObjectURL(link.href)
      })
    },

    wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
      return text.split("\n").flatMap((paragraph) => {
        const lines: string[] = []
        let line = ""
        paragraph.split(" ").forEach((word) => {
          const candidate = line ? `${line} ${word}` : word
          if (line && context.measureText(candidate).width > maxWidth) {
            lines.push(line)
            line = word
          } else {
            line = candidate
          }
        })
        return [...lines, line]
      })
    },
  },
}
</script>

<style lang="sass" scoped>
.sticky-board-dialog :deep(.v-overlay__content)
  pointer-events: none

.sticky-board
  position: fixed
  inset: 0
  overflow: hidden
  background-color: #FFFFFF
  pointer-events: auto

.sticky-board-sections
  position: absolute
  inset: 0
  display: grid

  &.count-2
    grid-template-columns: repeat(2, 1fr)

  &.count-3
    grid-template-columns: repeat(3, 1fr)

  &.count-4
    grid-template-columns: repeat(2, 1fr)
    grid-template-rows: repeat(2, 1fr)

.sticky-board-section
  display: flex
  align-items: center
  justify-content: center
  border-right: 1px solid rgba(0, 0, 0, 0.06)
  border-bottom: 1px solid rgba(0, 0, 0, 0.06)

.sticky-board-section-title
  padding: 4px 12px
  border-radius: 6px
  background-color: #FFFFFF
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1)
  font-size: 15px
  font-weight: 600
  color: #1F2937
</style>
